import { useCallback, useState } from 'react'

function isDropdownOpen(textarea: HTMLTextAreaElement): boolean {
  const dropdown = textarea.closest('.dropdown')
  if (!dropdown) return false
  const menu = dropdown.querySelector('.dropdown-menu')
  return menu ? menu.classList.contains('show') : false
}

function getDropdownItems(textarea: HTMLTextAreaElement): HTMLElement[] {
  const dropdown = textarea.closest('.dropdown')
  if (!dropdown) return []
  const menu = dropdown.querySelector('.dropdown-menu')
  if (!menu || !menu.classList.contains('show')) return []
  return Array.from(menu.querySelectorAll<HTMLElement>('.select2-results__option--selectable .select2-results__option__text'))
}

function isOnFirstLine(textarea: HTMLTextAreaElement): boolean {
  const pos = textarea.selectionStart
  return textarea.value.lastIndexOf('\n', pos - 1) === -1
}

function isOnLastLine(textarea: HTMLTextAreaElement): boolean {
  const pos = textarea.selectionStart
  return textarea.value.indexOf('\n', pos) === -1
}

function focusAtEnd(textarea: HTMLTextAreaElement) {
  textarea.focus()
  const len = textarea.value.length
  textarea.setSelectionRange(len, len)
}

function getAllTextareas(textarea: HTMLTextAreaElement): HTMLTextAreaElement[] {
  const container = textarea.closest('.table-responsive') || textarea.closest('.card-body')
  if (!container) return []
  return Array.from(container.querySelectorAll<HTMLTextAreaElement>('textarea[name="detail"]'))
}

export function useRequirementKeyboardNav() {
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const resetHighlight = useCallback(() => {
    setHighlightedIndex(-1)
  }, [])

  const handleKeyDown = useCallback((index: number, subIndex: number, e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget

    // Escape: close dropdown and reset highlight
    if (e.key === 'Escape') {
      if (isDropdownOpen(textarea)) {
        setHighlightedIndex(-1)
      }
      return
    }

    // Enter: select highlighted item if dropdown is open
    if (e.key === 'Enter') {
      if (isDropdownOpen(textarea)) {
        const items = getDropdownItems(textarea)
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          e.preventDefault()
          items[highlightedIndex].click()
          setHighlightedIndex(-1)
        }
      }
      return
    }

    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

    const isUp = e.key === 'ArrowUp'

    // Priority 1: dropdown open — navigate within suggestions
    if (isDropdownOpen(textarea)) {
      const items = getDropdownItems(textarea)
      if (items.length > 0) {
        e.preventDefault()
        const next = isUp
          ? (highlightedIndex <= 0 ? items.length - 1 : highlightedIndex - 1)
          : (highlightedIndex >= items.length - 1 ? 0 : highlightedIndex + 1)
        setHighlightedIndex(next)
        const li = items[next]?.closest('li')
        if (li) li.scrollIntoView({ block: 'nearest' })
      }
      return
    }

    // Priority 2: cursor in middle of multi-line text — native behavior
    if (isUp && !isOnFirstLine(textarea)) return
    if (!isUp && !isOnLastLine(textarea)) return

    // Priority 3: move to adjacent textarea via DOM
    const allTextareas = getAllTextareas(textarea)
    const currentIdx = allTextareas.indexOf(textarea)
    if (currentIdx === -1) {
      e.preventDefault()
      return
    }

    const targetIdx = isUp ? currentIdx - 1 : currentIdx + 1
    if (targetIdx < 0 || targetIdx >= allTextareas.length) {
      e.preventDefault()
      return
    }

    e.preventDefault()
    focusAtEnd(allTextareas[targetIdx])
  }, [highlightedIndex])

  return { highlightedIndex, setHighlightedIndex, resetHighlight, handleKeyDown }
}
